import { Button } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import PropTypes from 'prop-types'
import React from 'react'

import { Loading } from 'app/components/generic/LottieComponents'
import Table from 'app/components/generic/Table'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

import ApartmentFormModal from './ApartmentFormModal'
import ApartmentPageHeader from './ApartmentPageHeader'

const useStyles = makeStyles((theme) => ({
  // `overflowX: hidden` + `minWidth: 0` make this root a clipping box, so its
  // min-content width is 0 and the flex <main> (which has no min-width:0) can
  // shrink to the viewport instead of being stretched by a wide table. No
  // padding here — the header and the content area carry their own (matching
  // the main app), rather than one box of padding around the whole page.
  root: { minWidth: 0, overflowX: 'hidden' },
  header: { padding: theme.spacing(3, 3, 0) },
  content: { padding: theme.spacing(2, 0, 3) },
  // `compact`: for pages that already carry their own page header + tab strip
  // (At Home). Tighter top padding and no gap before the grid.
  headerCompact: { padding: theme.spacing(2, 3, 0) },
  contentCompact: { padding: theme.spacing(0, 0, 3) }
  // The table then scrolls inside its own horizontal scrollbar.
}))

const baseTableOptions = {
  selectableRows: 'none',
  filterType: 'dropdown',
  // Match the main system: the grid is end-to-end with ONE horizontal
  // scroller of its own and a fixed header. Do not wrap it in another
  // overflow container or you get two scrollbars.
  responsive: 'vertical',
  fixedHeader: true,
  tableBodyMaxHeight: 'calc(100vh - 320px)',
  print: false,
  download: true,
  viewColumns: false,
  rowsPerPage: 25,
  rowsPerPageOptions: [10, 15, 25, 100],
  elevation: 0
}

/**
 * Generic API-backed list page with real create / edit / delete. Each module
 * configures it with columns + form fields + the api fns. Pass refreshKey
 * (e.g. apartment.id) to refetch when a dependency changes.
 */
const ApartmentApiCrudPage = ({
  title,
  tableTitle,
  entityLabel,
  fetchList,
  createItem,
  updateItem,
  deleteItem,
  columns,
  fields,
  emptyValues,
  idOf = (row) => row.uuid,
  rowToForm = (row) => row,
  // Called from the click that opens the modal (never during render), so a page
  // can prime state its fields depend on — e.g. loading a parent's children
  // before the child dropdown renders. `row` is null when creating.
  onModalOpen,
  formToPayload = (values) => values,
  refreshKey,
  showSelector = true,
  headerAction,
  rowActions,
  // Optional: clicking a row (anywhere but its action buttons) calls this with
  // the row. Pages that do not pass it behave exactly as before.
  onRowClick,
  compact = false,
  subtitle,
  deleteLabel = 'Delete'
}) => {
  const classes = useStyles()
  const [rows, setRows] = React.useState(null)
  const [modal, setModal] = React.useState({ open: false, row: null })

  const openModal = (row) => {
    if (onModalOpen) onModalOpen(row)
    setModal({ open: true, row })
  }

  const load = React.useCallback(() => {
    let active = true
    setRows(null)
    fetchList()
      .then((res) => active && setRows(res))
      .catch(() => active && setRows([]))
    return () => {
      active = false
    }
  }, [fetchList])

  React.useEffect(load, [load, refreshKey])

  const submit = async (values) => {
    try {
      const payload = formToPayload(values)
      if (modal.row) await updateItem(idOf(modal.row), payload)
      else await createItem(payload)
      eventAlert(
        `${entityLabel} ${modal.row ? 'updated' : 'created'}`,
        constants.SUCCESS
      )
      setModal({ open: false, row: null })
      load()
    } catch (error) {
      // request.js already surfaces a toast
    }
  }

  const onDelete = async (row) => {
    try {
      await deleteItem(idOf(row))
      eventAlert(`${entityLabel} removed`, constants.SUCCESS)
      load()
    } catch (error) {
      // request.js already surfaces a toast
    }
  }

  if (!rows) return <Loading />

  const actionCol =
    updateItem || deleteItem || rowActions
      ? [
          {
            name: '',
            label: 'Actions',
            options: {
              filter: false,
              sort: false,
              download: false,
              customBodyRenderLite: (i) => (
                // Swallow the click so Edit / Delete never also fire the row's
                // own onRowClick.
                <span onClick={(e) => e.stopPropagation()}>
                  {updateItem && (
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => openModal(rows[i])}
                    >
                      Edit
                    </Button>
                  )}
                  {deleteItem && (
                    <Button size="small" onClick={() => onDelete(rows[i])}>
                      {deleteLabel}
                    </Button>
                  )}
                  {rowActions && rowActions(rows[i], load)}
                </span>
              )
            }
          }
        ]
      : []

  return (
    <div className={classes.root}>
      <div className={compact ? classes.headerCompact : classes.header}>
        <ApartmentPageHeader
          title={title}
          subtitle={subtitle}
          showSelector={showSelector}
          action={
            createItem ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => openModal(null)}
              >
                New {entityLabel}
              </Button>
            ) : (
              headerAction
            )
          }
        />
      </div>
      <div className={compact ? classes.contentCompact : classes.content}>
        <Table
          data={rows}
          columns={[...columns, ...actionCol]}
          options={
            onRowClick
              ? {
                  ...baseTableOptions,
                  onRowClick: (_, meta) => onRowClick(rows[meta.dataIndex]),
                  setRowProps: () => ({ style: { cursor: 'pointer' } })
                }
              : baseTableOptions
          }
          disableFullHeight
        />
      </div>
      {(createItem || updateItem) && (
        <ApartmentFormModal
          open={modal.open}
          title={`${modal.row ? 'Edit' : 'New'} ${entityLabel}`}
          // Fields flagged createOnly (e.g. opening stock that's afterwards
          // process-driven) are shown only when creating; editOnly ones (e.g.
          // a batch's status, which is always active at creation) only when
          // editing.
          fields={(() => {
            // `fields` may be a function of the row being edited — an
            // uncontrolled autocomplete needs that row to supply its
            // `initialValue`, or editing renders an empty box and saving
            // silently clears the value.
            const resolved =
              typeof fields === 'function' ? fields(modal.row) : fields || []
            return modal.row
              ? resolved.filter((f) => !f.createOnly)
              : resolved.filter((f) => !f.editOnly)
          })()}
          initialValues={modal.row ? rowToForm(modal.row) : emptyValues}
          onSubmit={submit}
          onClose={() => setModal({ open: false, row: null })}
        />
      )}
    </div>
  )
}

ApartmentApiCrudPage.propTypes = {
  title: PropTypes.string.isRequired,
  tableTitle: PropTypes.string.isRequired,
  entityLabel: PropTypes.string.isRequired,
  fetchList: PropTypes.func.isRequired,
  createItem: PropTypes.func,
  updateItem: PropTypes.func,
  deleteItem: PropTypes.func,
  columns: PropTypes.array.isRequired,
  fields: PropTypes.array,
  emptyValues: PropTypes.object,
  idOf: PropTypes.func,
  rowToForm: PropTypes.func,
  onModalOpen: PropTypes.func,
  formToPayload: PropTypes.func,
  refreshKey: PropTypes.any,
  showSelector: PropTypes.bool,
  headerAction: PropTypes.node,
  rowActions: PropTypes.func,
  onRowClick: PropTypes.func,
  compact: PropTypes.bool,
  subtitle: PropTypes.string,
  deleteLabel: PropTypes.string
}

export default ApartmentApiCrudPage
